import React, { useState, useEffect } from 'react';
import { FiAlertTriangle } from "react-icons/fi";
import { IoMdClose } from "react-icons/io";
import { IoLockOpenOutline , IoLockClosedOutline } from "react-icons/io5";
import Button from '../../../components/ui/Button';
import { strings } from '../../../strings/strings';

const FreezeModal = ({ isOpen, onClose, onConfirm, company }) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setReason('');
      setError('');
    }
  }, [isOpen]);

  if (!isOpen || !company) return null;

  const isFreezing = company.isActive;
  const Icon = isFreezing ? IoLockClosedOutline : IoLockOpenOutline;

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (isFreezing && !reason.trim()) {
      setError('Please provide a reason for freezing the company');
      return;
    }

    onConfirm(reason.trim());
    setReason('');
    setError('');
  };

  const handleClose = () => {
    setReason('');
    setError('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-gray-200 dark:border-gray-700 animate-slideUp">
        {/* Header */}
        <div
          className={`relative ${isFreezing ? "bg-orange-50 dark:bg-orange-900/20" : "bg-green-50 dark:bg-green-900/20"} border-b border-gray-200 dark:border-gray-700`}
        >
          <div className="flex items-center justify-between p-6">
            <div className="flex items-center gap-3">
              <div
                className={`p-3 ${isFreezing ? "bg-orange-100 dark:bg-orange-900/30" : "bg-green-100 dark:bg-green-900/30"} rounded-xl`}
              >
                <Icon
                  className={`w-6 h-6 ${isFreezing ? "text-orange-600 dark:text-orange-400" : "text-green-600 dark:text-green-400"}`}
                />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {isFreezing ? strings.FREEZE_COMPANY : strings.UNFREEZE_COMPANY}
                </h2>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              <IoMdClose className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-4">
            {/* Warning Message */}
            <div
              className={`flex gap-3 p-4 rounded-lg ${isFreezing ? "bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800" : "bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800"}`}
            >
              <FiAlertTriangle
                className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isFreezing ? "text-orange-600 dark:text-orange-400" : "text-green-600 dark:text-green-400"}`}
              />
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {isFreezing
                    ? `Are you sure you want to freeze ${company.companyName}?`
                    : `Are you sure you want to unfreeze ${company.companyName}?`}
                </p>
              </div>
            </div>

            {/* Reason Input - Only for Freezing */}
            {isFreezing && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {strings.REASON_FOR_FREEZING} <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value);
                    if (error) setError("");
                  }}
                  placeholder={strings.FREEZE_REASON_PLACEHOLDER}
                  rows={4}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    error
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-300 dark:border-gray-600 focus:ring-primary"
                  } bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 resize-none`}
                />
                {error && (
                  <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                    <FiAlertTriangle className="w-4 h-4" />
                    {error}
                  </p>
                )}
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{strings.FREEZE_REASON_DESCRIPTION}</p>
              </div>
            )}

            {/* Unfreeze Note */}
            {!isFreezing && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <span className="font-semibold">{strings.NOTE}</span> {strings.UNFREEZE_MESSAGE_DESCRIPTION}
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex gap-3 p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
            <Button type="button" variant="secondary" onClick={handleClose} className="flex-1">
              {strings.CANCEL}
            </Button>
            <Button type="submit" variant={isFreezing ? "warning" : "success"} className="flex-1">
              {isFreezing ? strings.FREEZE : strings.UNFREEZE}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FreezeModal;
